<script setup>
import { Head } from '@inertiajs/vue3'
import {
    PhUser, PhPhone, PhEnvelope, PhMapPin,
    PhCalendar, PhGraduationCap, PhIdentificationCard,
} from '@phosphor-icons/vue'
import Layout  from '@pages/Shared/layouts/StudentLayout.vue'
import userMale   from '@/../images/user-male.png'
import userFemale from '@/../images/user-female.png'

const props = defineProps({
    student:   Object,
    classRoom: Object,
    section:   Object,
    session:   Object,
})
</script>

<template>
    <Layout>
        <Head title="My Profile" />

        <div class="mb-8">
            <h1 class="text-2xl font-bold text-gray-900">My Profile</h1>
            <p class="text-gray-500 text-sm mt-1">Your personal and academic information</p>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">

            <!-- Profile card -->
            <div class="lg:col-span-1">
                <div class="bg-white rounded-lg border border-gray-200 p-6 text-center">
                    <img
                        class="w-24 h-24 rounded-full object-cover ring-4 ring-gray-100 mx-auto mb-4"
                        :src="student.avatar
                            ? `/storage/${student.avatar}`
                            : (student.gender?.toLowerCase() === 'male' ? userMale : userFemale)"
                        :alt="`${student.first_name} ${student.last_name}`"
                    />
                    <h2 class="text-lg font-bold text-gray-900">
                        {{ student.first_name }} {{ student.other_name }} {{ student.last_name }}
                    </h2>
                    <p class="text-sm text-gray-500 mt-0.5">{{ student.id }}</p>

                    <div class="mt-4 space-y-2 text-sm text-left">
                        <div class="flex items-center gap-3 text-gray-600">
                            <PhGraduationCap :size="15" class="text-primary shrink-0" />
                            <span>{{ classRoom?.name ?? '—' }}</span>
                        </div>
                        <div class="flex items-center gap-3 text-gray-600">
                            <PhIdentificationCard :size="15" class="text-primary shrink-0" />
                            <span class="font-mono text-xs">{{ student.id }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Details -->
            <div class="lg:col-span-2 space-y-5">

                <!-- Personal info -->
                <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <div class="px-5 py-4 border-b border-gray-100">
                        <h3 class="font-semibold text-gray-800 text-sm flex items-center gap-2">
                            <PhUser :size="15" class="text-primary" />
                            Personal Information
                        </h3>
                    </div>
                    <div class="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-gray-50">
                        <div v-for="row in [
                            { label: 'First Name',   value: student.first_name   },
                            { label: 'Last Name',    value: student.last_name    },
                            { label: 'Other Name',   value: student.other_name   },
                            { label: 'Date of Birth',value: student.date_of_birth },
                            { label: 'Gender',       value: student.gender       },
                            { label: 'Religion',     value: student.religion     },
                            { label: 'Nationality',  value: student.nationality  },
                            { label: 'State',        value: student.state_of_origin },
                            { label: 'LGA',          value: student.lga_of_origin   },
                        ]" :key="row.label"
                            class="px-5 py-3.5">
                            <p class="text-xs text-gray-400 mb-0.5">{{ row.label }}</p>
                            <p class="text-sm font-medium text-gray-800 capitalize">{{ row.value ?? '—' }}</p>
                        </div>
                    </div>
                </div>

                <!-- Contact -->
                <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <div class="px-5 py-4 border-b border-gray-100">
                        <h3 class="font-semibold text-gray-800 text-sm flex items-center gap-2">
                            <PhPhone :size="15" class="text-primary" />
                            Contact
                        </h3>
                    </div>
                    <div class="divide-y divide-gray-50">
                        <div v-for="row in [
                            { icon: PhPhone,    label: 'Phone',   value: student.phone   },
                            { icon: PhEnvelope, label: 'Email',   value: student.email   },
                            { icon: PhMapPin,   label: 'Address', value: student.address },
                        ]" :key="row.label"
                            class="px-5 py-3.5 flex items-start gap-3">
                            <component :is="row.icon" :size="15" class="text-gray-400 mt-0.5 shrink-0" />
                            <div>
                                <p class="text-xs text-gray-400 mb-0.5">{{ row.label }}</p>
                                <p class="text-sm text-gray-800">{{ row.value ?? '—' }}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Guardian -->
                <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <div class="px-5 py-4 border-b border-gray-100">
                        <h3 class="font-semibold text-gray-800 text-sm flex items-center gap-2">
                            <PhUser :size="15" class="text-primary" />
                            Guardian / Parent
                        </h3>
                    </div>
                    <div class="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-gray-50">
                        <div v-for="row in [
                            { label: 'Name',         value: student.guardian_name         },
                            { label: 'Phone',        value: student.guardian_phone        },
                            { label: 'Relationship', value: student.guardian_relationship },
                            { label: 'Occupation',   value: student.guardian_occupation   },
                        ]" :key="row.label" class="px-5 py-3.5">
                            <p class="text-xs text-gray-400 mb-0.5">{{ row.label }}</p>
                            <p class="text-sm font-medium text-gray-800">{{ row.value ?? '—' }}</p>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    </Layout>
</template>